/// <reference types="vite/client" />
import * as Colyseus from 'colyseus.js';

/**
 * Single shared Colyseus client. The server lives on its own port (8788) in
 * dev; in production it sits behind the same TLS-terminating reverse proxy as
 * the Fastify API, so the URL switches to wss://yourdomain.com.
 *
 * The session cookie set on /api endpoints is sent automatically on the
 * WebSocket upgrade (cookies are scoped by domain, not by port — RFC 6265).
 */
const COLYSEUS_URL =
  import.meta.env.VITE_COLYSEUS_URL ??
  (location.hostname === 'localhost' || location.hostname === '127.0.0.1'
    ? `ws://${location.hostname}:8788`
    : `wss://${location.hostname}`);

export const colyseusClient = new Colyseus.Client(COLYSEUS_URL);

/** Re-export Room so callers don't need to import colyseus.js separately. */
export type { Room } from 'colyseus.js';

// ----------------------------------------------------------------------------
// Lobby room state mirror — these interfaces describe what the schema sends.
// We don't import the server-side schema classes (server/client share would
// require a shared package). Instead, we type the state shape manually.

export type SlotType =
  | 'open'
  | 'human'
  | 'ai_easy'
  | 'ai_medium'
  | 'ai_hard'
  | 'closed';

export type ParticipantType = 'human' | 'ai_easy' | 'ai_medium' | 'ai_hard' | 'barbarian';
export type Phase = 'lobby' | 'dice_roll' | 'placement' | 'playing' | 'ended';

export interface RegionUnits {
  warriors: number;
  archers: number;
  berserkers: number;
  crossbowmen: number;
  paladins: number;
  heroes: number;
  kamikazes: number;
  movedThisTurn: boolean;
  /** True if this castle/camp already recruited (or was built) this turn.
   *  Per-region — recruiting at one castle doesn't lock the others. */
  recruitedThisTurn: boolean;
}

export interface PlayerSlot {
  index: number;
  type: SlotType;
  id: string;
  name: string;
  factionId: string;
  ready: boolean;
  isHost: boolean;
}

export interface Participant {
  index: number;
  id: string;
  name: string;
  factionId: string;
  type: ParticipantType;
  diceRoll: number;
  /** Sequence of every roll this participant performed, in round order.
   *  Length ≥ 1. Participants involved in tie-breakers have multiple entries.
   *  Read via Colyseus' ArraySchema interface (has length + indexed access). */
  diceRollHistory: { length: number; [index: number]: number };
  /** Mission IDs this participant has already completed in this game. */
  completedMissionIds: { length: number; [index: number]: string };
  /** Game-turn number at which this participant may receive their next
   *  mission. 0 = no cooldown active. While `missionId === '' &&
   *  turnNumber < nextMissionTurn`, the mission card is "locked". */
  nextMissionTurn: number;
  turnOrder: number;
  // Resources
  wood: number;
  stone: number;
  gold: number;
  iron: number;
  silver: number;
  fruit: number;
  regionsOwned: number;
  continentsOwned: number;
  castleTier: number;
  hasRecruitedThisTurn: boolean;
  aiPersonality: string;
  aiBehavior: string;
  researchedTechs: SchemaArray<string>;
  isEliminated: boolean;
  attackBuffTurns: number;
  defenseBuffTurns: number;
  siesteBuffTurns: number;
  attackedThisTurn: boolean;
  kamikazeRecruitedThisTurn: boolean;
  castleShots: number;
  missionId: string;
  missionsCompleted: number;
  /** True while the participant's client connection is gone (reconnect
   *  pending). UI can grey out their row / show a "déco" badge. */
  isDisconnected: boolean;
  /** Ranked-mode only: wall-clock ms when this participant will be
   *  forfeited if they haven't rejoined. 0 = no pending forfeit. */
  disconnectExpiresEpoch: number;
  /** Ranked-mode only: hex colour tinted onto the head sprite so two
   *  players who picked the same kingdom remain distinguishable. */
  colorTint: string;
}

export interface MissionReward {
  wood?: number; stone?: number; gold?: number; iron?: number; silver?: number;
  fruit?: number;
  castleShots?: number;
  attackBuffTurns?: number;
  defenseBuffTurns?: number;
}

export interface MissionDef {
  id: string;
  name: string;
  icon: string;
  description: string;
  reward: MissionReward;
}

export const MISSIONS: Record<string, MissionDef> = {
  fiveRegions: { id: 'fiveRegions',  name: 'Patrouille de quartier',  icon: '/sprites/iconchat_neutre.png',     description: 'Possède 5 coins de jardin en même temps.',     reward: { fruit: 15, gold: 10 } },
  tenRegions:  { id: 'tenRegions',   name: 'Maître du quartier',      icon: '/sprites/crown.png',                description: 'Possède 10 coins de jardin en même temps.',    reward: { fruit: 30, gold: 20, castleShots: 1 } },
  ownContinent:{ id: 'ownContinent', name: 'Conquête de continent',   icon: '/sprites/chateau_tier2.png',        description: 'Possède TOUT un continent à toi seul.',        reward: { fruit: 40, iron: 5, silver: 1 } },
  castleT2:    { id: 'castleT2',     name: 'Renforcement de repaire', icon: '/sprites/chateau_tier2.png',        description: 'Améliore un repaire au niveau 2.',             reward: { fruit: 20, stone: 15 } },
  castleT3:    { id: 'castleT3',     name: 'Forteresse royale',       icon: '/sprites/chateau_tier3.png',        description: 'Améliore un repaire au niveau 3.',             reward: { fruit: 40, iron: 8, attackBuffTurns: 3 } },
  buildCamp:   { id: 'buildCamp',    name: 'Cachette de chat',        icon: '/sprites/camp.png',                 description: 'Construis au moins 1 coin secret (camp).',     reward: { fruit: 12, wood: 10 } },
  threeTechs:  { id: 'threeTechs',   name: 'Chat curieux',            icon: '/sprites/deco_feuille.png',         description: 'Sniffe au moins 3 idées dans l\'arbre.',       reward: { fruit: 25, gold: 15, defenseBuffTurns: 3 } },
  buyBoost:    { id: 'buyBoost',     name: 'Croquettes magiques',     icon: '/sprites/ressource_fruit.png',      description: 'Achète au moins 1 croquette magique.',         reward: { fruit: 15, castleShots: 1 } },
};

/** Minimal contract over Colyseus's ArraySchema for our purposes. */
interface SchemaArray<T> {
  length: number;
  [i: number]: T | undefined;
  forEach(cb: (item: T, i: number) => void): void;
}

export interface LobbyState {
  name: string;
  phase: Phase;
  isPrivate: boolean;
  hostUserId: string;
  seed: number;
  slots: SchemaArray<PlayerSlot>;
  /** True when this room was spawned by the matchmaker for a ranked
   *  match. Drives ranked-specific UI bits (badge, timer, tinted heads). */
  isRanked: boolean;
  /** Ranked-mode only: wall-clock epoch (ms) when the current player's
   *  turn auto-expires. 0 outside ranked or playing. */
  turnDeadlineEpoch: number;

  // ---- Game phase additions ----
  participants: SchemaArray<Participant>;
  mapWidth: number;
  mapHeight: number;
  mapMaxRegions: number;
  /** Number of continents to generate on the map (3-12). */
  mapContinentCount: number;
  /** Host-toggleable: whether barbarian raid events may fire in this
   *  game. Default true. Mirrored from server `state.barbariansEnabled`. */
  barbariansEnabled: boolean;
  /** -1 = unowned, otherwise participant index. */
  regionOwners: SchemaArray<number>;
  /** regionId per participant (-1 = not placed). */
  castles: SchemaArray<number>;
  /** Index in turn order (0..N-1) of the current participant. */
  currentTurnIndex: number;
  /** Dice ceremony cursor — `participant.index` of the player whose
   *  turn it is to roll, or -1 outside the dice phase. */
  diceCurrentIndex: number;
  /** 0 during placement, 1+ once playing. */
  turnNumber: number;
  /** Units per region. */
  regionUnits: SchemaArray<RegionUnits>;
  /** Display name of each continent (indexed by continentId). */
  continentNames: SchemaArray<string>;
  /** Per-region label like "Miaoumie #3". */
  regionLabels: SchemaArray<string>;
  /** Per-region building tag (0=none, 1=camp, 2=castle T1, 3=T2, 4=T3). */
  regionBuilding: SchemaArray<number>;
  /** Index of the winning participant, or -1 if no winner yet. */
  winnerIndex: number;
  /** Active "Pattée spéciale" drop region (-1 = none). */
  dropRegionId: number;
  /** Turn the drop expires on. */
  dropExpiresOnTurn: number;
  /** Active random event tag ('' = none, 'fish_rain', 'big_sleep', 'barbarian_raid'). */
  activeEvent: string;
  /** Turn the active event expires on. */
  eventExpiresOnTurn: number;
  /** Index of the barbarian raid participant (-1 = no barbarians right now).
   *  When set, `participants[barbarianParticipantIndex]` is the NPC raider
   *  that uses the `barbare` faction sprites + iconchat_barbare. */
  barbarianParticipantIndex: number;
}

/** Building tags — mirror of server constants. */
export const BUILDING_NONE = 0;
export const BUILDING_CAMP = 1;
export const BUILDING_CASTLE_T1 = 2;
export const BUILDING_CASTLE_T2 = 3;
export const BUILDING_CASTLE_T3 = 4;

/** Tech-tree mirror. KEEP IN SYNC with server/src/colyseus/lobby_room.ts. */
export interface TechDef {
  id: string;
  branch: 'def' | 'eco' | 'mil' | 'utl';
  tier: 1 | 2 | 3;
  name: string;
  icon: string;
  description: string;
  costFruit: number;
  requiresTechs: string[];
  requiresRegions: number;
}

/** Consumable boost catalog. Mirror of server/src/colyseus/lobby_room.ts. */
export interface BoostDef {
  id: string;
  name: string;
  icon: string;
  description: string;
  costFruit: number;
  requiresRegions: number;
}

export const BOOSTS: Record<string, BoostDef> = {
  attackBuff: { id: 'attackBuff', name: 'Coup de griffe royal',     icon: '/sprites/arme_hache_combat.png',    description: '+25 % attaque pour TOUTES mes meutes pendant 3 patrouilles. Empilable.',                costFruit: 30, requiresRegions: 3 },
  defenseBuff:{ id: 'defenseBuff', name: 'Posture du chat couché',  icon: '/sprites/arme_bouclier_pierre.png', description: '+25 % défense pour TOUTES mes positions pendant 3 patrouilles. Empilable.',             costFruit: 30, requiresRegions: 3 },
  castleShot: { id: 'castleShot', name: 'Défense de la litière',    icon: '/sprites/arme_warshooter_bois.png', description: '+1 charge. Tire une volée (~12 archers) depuis un repaire sur une cible à portée 2.',  costFruit: 25, requiresRegions: 0 },
  fullHeal:   { id: 'fullHeal', name: 'Sieste réparatrice',         icon: '/sprites/iconchat_valide.png',      description: '+1 unité de chaque type déjà présent dans TOUTES mes régions (one-shot).',             costFruit: 40, requiresRegions: 5 },
};

export const TECHS: Record<string, TechDef> = {
  palissade:  { id: 'palissade',  branch: 'def', tier: 1, name: 'Buisson piquant',       icon: '/sprites/ressource_bois.png',          description: 'Tous mes coins de jardin : +10% PV en défense.', costFruit: 12, requiresTechs: [], requiresRegions: 0 },
  scierie:    { id: 'scierie',    branch: 'eco', tier: 1, name: 'Grignote-bois',         icon: '/sprites/arme_hache_pierre.png',       description: '+1 bout de bois/tour par coin que je possède.', costFruit: 18, requiresTechs: [], requiresRegions: 3 },
  caserne:    { id: 'caserne',    branch: 'mil', tier: 1, name: 'Niche d\'entraînement', icon: '/sprites/arme_epee.png',               description: '+20% PV pour les chatons dans mes repaires et coins secrets.', costFruit: 22, requiresTechs: [], requiresRegions: 0 },
  arcs:       { id: 'arcs',       branch: 'utl', tier: 1, name: 'Atelier de griffes',    icon: '/sprites/arme_arc_bois.png',           description: 'Portée 3 pour les pêcheurs d\'oiseaux et tireurs précis postés sur un camp ou un repaire.', costFruit: 40, requiresTechs: [], requiresRegions: 4 },
  murs:       { id: 'murs',       branch: 'def', tier: 2, name: 'Murs en cailloux',      icon: '/sprites/ressource_pierre.png',        description: 'Tous mes coins : +25% PV (remplace Buisson piquant).', costFruit: 35, requiresTechs: ['palissade'], requiresRegions: 0 },
  marche:     { id: 'marche',     branch: 'eco', tier: 2, name: 'Marché aux poissons',   icon: '/sprites/piece_or.png',                description: '+1 pièce brillante/tour par coin adjacent à un repaire.', costFruit: 35, requiresTechs: ['scierie'], requiresRegions: 0 },
  garnison:   { id: 'garnison',   branch: 'mil', tier: 2, name: 'Aristochaterie',        icon: '/sprites/arme_bouclier_pierre.png',    description: 'Débloque les aristochats (repaire niv. 2+).', costFruit: 45, requiresTechs: ['caserne'], requiresRegions: 0 },
  banque:     { id: 'banque',     branch: 'eco', tier: 3, name: 'Coffre royal',          icon: '/sprites/chateau_tier3.png',           description: '+2 pièces brillantes/tour par adjacent à un repaire + 1 brillant argenté passif.', costFruit: 70, requiresTechs: ['marche'], requiresRegions: 10 },
  sanctuaire: { id: 'sanctuaire', branch: 'mil', tier: 3, name: 'Trône de la Légende',   icon: '/sprites/crown.png',                   description: 'Débloque la Légende (repaire niv. 3, unique). NIA suprême.', costFruit: 80, requiresTechs: ['garnison'], requiresRegions: 8 },
  hopital:    { id: 'hopital',    branch: 'utl', tier: 3, name: 'Infirmerie ronronnante',icon: '/sprites/iconchat_valide.png',         description: 'Régen 25% PV/tour dans mes repaires niv. 3. Prrrr.', costFruit: 55, requiresTechs: [], requiresRegions: 7 },
  sieste:     { id: 'sieste',     branch: 'def', tier: 2, name: 'Sieste collective',     icon: '/sprites/iconchat_dodo.png',           description: 'Si je n\'attaque personne durant un tour, défense ×1.5 le tour suivant. Ronronnement obligatoire.', costFruit: 30, requiresTechs: [], requiresRegions: 4 },
};
